import { Button } from "@/modern/components/ui/button";
import { confirmDialog } from "@/react/utils/confirmDialogBridge";
import { getFormattedFileSize } from "@/other/utils/utils";

export interface ConfirmScheduleFileDropParams {
  files: File[];
  taskName: string;
}

const MAX_FILE_NAMES_SHOWN = 5;

function FileList({ files }: { files: File[] }) {
  const shown = files.slice(0, MAX_FILE_NAMES_SHOWN);
  const remaining = files.length - shown.length;

  return (
    <div className="space-y-1.5 text-left">
      <p className="text-sm font-medium text-foreground">Files</p>
      <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
        {shown.map((file) => {
          const size = file.size ? getFormattedFileSize(file.size) : "";
          return (
            <li key={`${file.name}-${file.size}-${file.lastModified}`} className="break-words">
              {file.name}
              {size ? <span className="text-muted-foreground ml-1.5">({size})</span> : null}
            </li>
          );
        })}
        {remaining > 0 ? (
          <li className="text-muted-foreground list-none -ml-5">…and {remaining} more</li>
        ) : null}
      </ul>
    </div>
  );
}

/**
 * Confirm before schedule file upload. Snapshot `files` before awaiting —
 * `FileList` from the drop event can clear once a modal opens.
 */
export async function confirmScheduleFileDrop({
  files,
  taskName,
}: ConfirmScheduleFileDropParams): Promise<boolean> {
  if (files.length === 0) return false;

  const fileCount = files.length;
  const title = fileCount === 1 ? "Upload file to task?" : `Upload ${fileCount} files to task?`;
  const taskLabel = taskName || "Untitled task";
  const descriptionCopy =
    fileCount === 1
      ? <>Upload this file to <span className="text-foreground font-medium">{taskLabel}</span> task</>
      : <>Upload these files to <span className="text-foreground font-medium">{taskLabel}</span> task</>;

  return confirmDialog({
    title,
    description: (
      <div className="space-y-3 text-left text-sm text-muted-foreground">
        <p>{descriptionCopy}</p>
        <FileList files={files} />
      </div>
    ),
    customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
      <div className="flex w-full justify-end gap-2">
        <Button variant="ghost" onClick={cancel}>
          Cancel
        </Button>
        <Button onClick={confirm}>Upload</Button>
      </div>
    ),
  });
}
